import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import useAuctionNegotiation from '../../../../../_shared/hooks/trade/useAuctionNegotiation';
import { getNegotiationState } from '../../../../../_shared/models/NegotiationList';
import TradeDetails from '../../../../../_shared/models/TradeDetails';
import { TradeContext } from '../../../../../_shared/types/trade';
import NegotiationButtonVariant from '../../../../../_shared/constants/negotiationButtonVariant';
import NegotiationState from '../../../../../_shared/constants/negotiationState';

import PendingBidBoxFooterContext from './PendingBidBoxFooterContext';
import BidBoxContext from '../../../BidBoxContainer/BidBoxContext';
import PendingBidBoxFooter from '../PendingBidBoxFooter';
import NegotiationPosition from '../../../../../_shared/constants/negotiationPosition';
import ROUTES from '../../../../Routes/constants';

const PendingBidBoxFooterContainer = ({ isOmp = false, tradeContext }) => {
  const history = useHistory();
  const { tradeDetails } = useContext(BidBoxContext);

  const negotiationPosition = tradeDetails?.needMoreStatus?.negotiationPosition;
  const negotiations = useAuctionNegotiation(
    tradeDetails?.auctionSummary?.id,
    TradeDetails.canNegotiate(tradeDetails)
  );
  const [negotiationState, setNegotiationState] = useState(null);
  const [negotiationButtonVariant, setNegotiationButtonVariant] = useState(
    null
  );
  const [pendingBidBoxFooterContext, setPendingBidBoxFooterContext] = useState({
    negotiations,
    negotiationState: null,
    negotiationButtonVariant: null
  });
  const user = useSelector(state => state?.auth?.user, shallowEqual);
  const isCsu = user?.superUserId && user?.superUserId !== user?.id;

  const buttonMap = useMemo(() => {
    const isSecondBidder =
      negotiationPosition === NegotiationPosition.IS_SECOND_BIDDER &&
      negotiationState === NegotiationState.FINAL_ASKING_PRICE;

    return {
      [TradeContext.Sell]: {
        [NegotiationState.NO_NEGOTIATION]: NegotiationButtonVariant.close,
        [NegotiationState.BUYER_OFFER]: NegotiationButtonVariant.respond,
        [NegotiationState.BUYER_COUNTER]: NegotiationButtonVariant.respond,
        [NegotiationState.BUYER_FIRM]: NegotiationButtonVariant.respond,
        [NegotiationState.SELLER_OFFER]: NegotiationButtonVariant.view,
        [NegotiationState.SELLER_COUNTER]: NegotiationButtonVariant.view,
        [NegotiationState.FINAL_ASKING_PRICE]: NegotiationButtonVariant.view
      },
      [TradeContext.Buy]: {
        [NegotiationState.NO_NEGOTIATION]: NegotiationButtonVariant.start,
        [NegotiationState.BUYER_OFFER]: NegotiationButtonVariant.view,
        [NegotiationState.BUYER_COUNTER]: NegotiationButtonVariant.view,
        [NegotiationState.BUYER_FIRM]: NegotiationButtonVariant.view,
        [NegotiationState.SELLER_OFFER]: NegotiationButtonVariant.respond,
        [NegotiationState.SELLER_COUNTER]: NegotiationButtonVariant.respond,
        [NegotiationState.FINAL_ASKING_PRICE]: isSecondBidder
          ? NegotiationButtonVariant.respond
          : NegotiationButtonVariant.view
      }
    };
  }, [negotiationPosition, negotiationState]);

  // Check if user is eligible to visit this page
  useEffect(() => {
    if (isCsu !== undefined && negotiationState && negotiationPosition) {
      const isDealer =
        tradeContext === TradeContext.Sell &&
        negotiationPosition === NegotiationPosition.IS_DEALER;

      const isTopBidder =
        negotiationPosition === NegotiationPosition.IS_TOP_BIDDER &&
        ![
          NegotiationState.DECLINED,
          NegotiationState.FINAL_ASKING_PRICE
        ].includes(negotiationState);

      const isSecondBidder =
        negotiationPosition === NegotiationPosition.IS_SECOND_BIDDER &&
        negotiationState === NegotiationState.FINAL_ASKING_PRICE;

      const userAllowedPendingVDP =
        isCsu || isDealer || isTopBidder || isSecondBidder;

      if (!userAllowedPendingVDP) {
        const listViewRedirectTimeout = setTimeout(() => {
          if (tradeContext === TradeContext.Sell) {
            history.push(`${ROUTES.SELLING_PENDING}`);
          } else {
            history.push(`${ROUTES.BUYING_PENDING}`);
          }
        }, 1000);

        return () => {
          clearTimeout(listViewRedirectTimeout);
        };
      }
    }

    return () => {};
  }, [tradeContext, history, isCsu, negotiationPosition, negotiationState]);

  // Set negotiationState after loading negotiations
  useEffect(() => {
    if (negotiations) {
      setNegotiationState(getNegotiationState(negotiations));
    } else if (!TradeDetails.canNegotiate(tradeDetails)) {
      setNegotiationState(
        TradeDetails.getNegotiationState(tradeDetails, false)
      );
    }
  }, [tradeDetails, negotiations]);

  // Set negotiationButtonVariant after setting negotiationState
  useEffect(() => {
    if (tradeContext && negotiationState && negotiationPosition) {
      const isNegotiationBtnBlocked =
        negotiationPosition === NegotiationPosition.IS_SECOND_BIDDER &&
        negotiationState === NegotiationState.NO_NEGOTIATION;
      if (!isNegotiationBtnBlocked) {
        const newNegotiationButtonVariant =
          buttonMap[tradeContext][negotiationState];

        setNegotiationButtonVariant(newNegotiationButtonVariant);
      }
    }
  }, [tradeContext, negotiationState, negotiationPosition, buttonMap]);

  // Set pendingBidBoxFooterContext after setting other state vars
  useEffect(() => {
    if (negotiations && negotiationState && negotiationButtonVariant) {
      setPendingBidBoxFooterContext({
        negotiations,
        negotiationState,
        negotiationButtonVariant
      });
    }
  }, [negotiations, negotiationState, negotiationButtonVariant]);

  return (
    <PendingBidBoxFooterContext.Provider value={pendingBidBoxFooterContext}>
      <PendingBidBoxFooter isOmp={isOmp} tradeContext={tradeContext} />
    </PendingBidBoxFooterContext.Provider>
  );
};

export default PendingBidBoxFooterContainer;
